import { createStore, useStore } from 'zustand';

export interface BreakpointValues {
  base: boolean;
  sm: boolean;
  lg: boolean;
  xl: boolean;
  xl2: boolean;
  xl3: boolean;
  isUltra: boolean;
  isDesktop: boolean;
  isTablet: boolean;
  isMobile: boolean;
  isHydrated: boolean;
}

const breakpoints = {
  base: 0,
  sm: 768,
  lg: 1024,
  xl: 1280,
  xl2: 1440,
  xl3: 1800,
} as const;

const calulateBreakpointValues = (width: number | undefined) => {
  const isHydrated = width != null;
  const { base, sm, lg, xl, xl2, xl3 } = {
    base: isHydrated && width >= breakpoints.base,
    sm: isHydrated && width >= breakpoints.sm,
    lg: isHydrated && width >= breakpoints.lg,
    xl: isHydrated && width >= breakpoints.xl,
    xl2: isHydrated && width >= breakpoints.xl2,
    xl3: isHydrated && width >= breakpoints.xl3,
  };
  const isUltra = xl3;
  const isDesktop = !xl3 && xl;
  const isTablet = !xl && sm;
  const isMobile = !sm && base;
  return {
    isHydrated,
    isMobile,
    isTablet,
    isDesktop,
    isUltra,
    sm,
    lg,
    xl,
    xl2,
    xl3,
    base,
  } satisfies BreakpointValues;
};

interface ScreenSizeStore {
  size: { width: number; height: number };
  breakpointValues: BreakpointValues;
  setSize: (size: { width: number; height: number }) => void;
  recalculateBreakpointValues: () => void;
}

export const screensizeStore = createStore<ScreenSizeStore>((set) => ({
  breakpointValues: {
    isHydrated: false,
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isUltra: false,
    sm: false,
    lg: false,
    xl: false,
    xl2: false,
    xl3: false,
    base: false,
  },
  size:
    typeof window !== 'undefined'
      ? {
          width: window.innerWidth,
          height: window.innerHeight,
        }
      : {
          width: 0,
          height: 0,
        },
  setSize: ({ width, height }) =>
    set({
      size: { width, height },
      breakpointValues: calulateBreakpointValues(window.innerWidth),
    }),
  recalculateBreakpointValues: () =>
    set({
      size: { width: window.innerWidth, height: window.innerHeight },
      breakpointValues: calulateBreakpointValues(window.innerWidth),
    }),
}));

export const useScreenSize = <T>(selector: (state: ScreenSizeStore) => T) =>
  useStore(screensizeStore, selector);
